import { useMemo } from "react";
import { useAtom } from "jotai";
import { orderBy } from "lodash-es";
import { useTranslation } from "react-i18next";
import { ListItem, ListItemText } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import type {
  DropResult,
  ResponderProvided,
  DraggingStyle,
  NotDraggingStyle,
  DroppableProvided,
  DroppableStateSnapshot,
  DraggableProvided,
  DraggableStateSnapshot,
} from "react-beautiful-dnd";
import type { Map as LeafletMap } from "leaflet";

import type { Overlayer, LayerManagerContext } from "src/types";
import { activeOverlayersAtom } from "src/atoms/overlayers";
import { ActiveLayerItem } from "./ActiveLayerItem";
import { filterByContext, filterActive, updateOverlayer } from "src/utils/layer-manager.utils";
import { reorder, updateListOrder } from "src/utils/sort.utils";
import { pipe } from "src/utils/fp.utils";

// React 18 JSX types are incompatible with react-beautiful-dnd class components
// biome-ignore lint/suspicious/noExplicitAny: library class components lack React 18 refs type
const DndContext = DragDropContext as React.ComponentType<any>;
// biome-ignore lint/suspicious/noExplicitAny: library class components lack React 18 refs type
const DndDroppable = Droppable as React.ComponentType<any>;
// biome-ignore lint/suspicious/noExplicitAny: library class components lack React 18 refs type
const DndDraggable = Draggable as React.ComponentType<any>;

interface Props {
  context: LayerManagerContext;
  map: LeafletMap;
}

export function ActiveLayers(props: Props) {
  const [activeLayers, setActiveLayers] = useAtom(activeOverlayersAtom);

  const { t } = useTranslation();

  /**
   * Memo
   */
  // TODO: this can be reused?
  const overlayers = useMemo((): Overlayer[] => {
    const sort = (overlayers: Overlayer[]) => orderBy(overlayers, "zIndex", "desc");

    return pipe(filterActive, filterByContext(props.context), sort)(activeLayers);
  }, [activeLayers, props.context]);

  /**
   * Functions
   */
  function onDragEnd(result: DropResult, provided: ResponderProvided) {
    if (!result.destination) return;

    const reorderedOverlayers = reorder(overlayers, result.source.index, result.destination.index);

    setActiveLayers(updateListOrder<Overlayer>(activeLayers, reorderedOverlayers));
  }

  function handleLayerChange(update: Overlayer) {
    setActiveLayers(updateOverlayer(activeLayers, update));
  }

  return render();

  function render() {
    return (
      <Styles>
        <ListItem className="list-header">
          <ListItemText primary={t("label.activeLayers")} color="primary" disableTypography />
        </ListItem>

        <DndContext onDragEnd={onDragEnd}>
          <DndDroppable droppableId="droppable">
            {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {overlayers.map((layer, index) => (
                  <DndDraggable key={layer.key} draggableId={layer.key} index={index}>
                    {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                      <ActiveLayerItem
                        ref={provided.innerRef}
                        key={layer.key}
                        overlayer={layer}
                        onChange={handleLayerChange}
                        draggableProps={provided.draggableProps}
                        dragHandleProps={provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      />
                    )}
                  </DndDraggable>
                ))}
                {provided.placeholder as React.ReactNode}
              </div>
            )}
          </DndDroppable>
        </DndContext>
      </Styles>
    );
  }
}

function getItemStyle(
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined
): React.CSSProperties {
  // some basic styles to make the items look a bit nicer
  return {
    userSelect: "none",
    transition: "all 300ms",

    background: isDragging ? "rgba(247, 247, 247, 90%)" : "inherit",
    border: isDragging ? "1px solid rgba(0, 0, 0, 0.35)" : "none",
    boxShadow: isDragging ? "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px" : "none",

    // styles we need to apply on draggables
    ...draggableStyle,
  };
}

const Styles = styled("div")`
  padding: ${({ theme }) => theme.spacing(0, 0, 1, 0)};
  overflow: auto;
  flex-shrink: 0;
  width: 100%;
  height: fit-content;
  max-height: 50%;
  background-color: #f7f8f9;
  border-top: 1px solid #e7e7e7;

  .list-header {
    padding: ${({ theme }) => theme.spacing(2, 3, 2, 2)};

    .MuiListItemText-root {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;
